// The redesigned Benefit Verification view, read from Stedi's new-format responses
// (`benefits_eligibility_result.newFormat`).
//
// Every rule here comes from the Benefit Verification field map
// (https://claude.ai/artifact/9qfhRdV6KMhbAXjVEgnhRZ). The one that matters most: translate, never
// conclude. Values show as returned, in plain words; a value the payer didn't send reads "Not returned
// by payer", and nothing is derived (no plan type, payer order or amount the payer didn't send).
//
// Paths the field map marks "to confirm" are read where real responses put them, and fall back to
// "Not returned by payer" otherwise: group number, patient liability and other payers.

import { startCase } from 'lodash';

import { extractDigits, formatPhone10Digits } from 'lib/phone';

import { benefitCoverageText, formatStediDate, SNF_STC_CODES } from './eligibilityData';

export const NOT_RETURNED = 'Not returned by payer';
export const SNF_NOT_REPORTED = 'Skilled nursing benefits were not reported by this payer.';
export const SNF_NOT_CHECKED = "The skilled nursing check didn't return a result, so its benefits can't be shown here.";

const PLAN_LEVEL = '30';
// Wider than the field map's AG / AH: the same codes today's view counts as skilled nursing, so A2
// (long-term care) lines don't disappear for a payer that reports skilled nursing only there.
const SKILLED_NURSING = new Set(SNF_STC_CODES);

// ---- The response slice the view reads ----
export interface NfService {
  value?: string;
  definition?: string;
  system?: string;
}

export interface NfName {
  organization?: string;
  person?: { firstName?: string; lastName?: string };
}

export interface NfRelatedEntity {
  type?: string;
  name?: NfName;
  memberId?: string;
  contacts?: { phoneNumbers?: string[] }[];
}

export interface NfDates {
  start?: string;
  end?: string;
}

export interface NfEntry {
  service?: NfService;
  status?: string;
  network?: { indicator?: string };
  coverageLevel?: string;
  insuranceType?: string;
  planCoverageDescription?: string;
  timePeriod?: string;
  amount?: string;
  percent?: string;
  quantity?: { qualifier?: string; value?: string };
  serviceLimits?: NfServiceLimit[];
  messages?: string[];
  priorAuthIndicator?: string;
  dates?: Record<string, NfDates | undefined>;
  relatedEntities?: NfRelatedEntity[];
  invalidReasons?: { description?: string }[];
}

// Where a payer puts day counts other than `quantity`. `period.qualifier` is REMAINING for days left,
// NOT_EXCEEDED / EXCEEDED for a copay tier's bounds, or a period (CALENDAR_YEAR) for a limit.
// Payers have been seen sending the day count inside delivery (Aetna); it's read from either place.
export interface NfServiceLimit {
  quantity?: NfQuantity;
  delivery?: { quantity?: NfQuantity; period?: { qualifier?: string; value?: number | string } };
}

interface NfQuantity {
  qualifier?: string;
  value?: string;
}

export interface NfPlan {
  name?: string;
  benefits?: Record<string, NfEntry[] | Record<string, NfEntry[]> | undefined>;
  relatedEntities?: NfRelatedEntity[];
}

export interface NfResponse {
  id?: string;
  payer?: { name?: NfName };
  subscriber?: {
    memberId?: string;
    dateOfBirth?: string;
    dates?: Record<string, NfDates | undefined>;
    additionalInformation?: Record<string, { number?: string; name?: string } | undefined>;
  };
  dependent?: NfResponse['subscriber'];
  plans?: NfPlan[];
}

export interface NfCheck {
  serviceTypeCodes?: string[];
  eligibilityCheckId?: string | null;
  stediResponse?: NfResponse;
}

// Why skilledNursing is or isn't there. not_run (held back while Stedi was busy) and failed mean the
// payer was never asked on the new endpoint, so its silence says nothing. Absent on older results.
export type SkilledNursingStatus = 'returned' | 'not_needed' | 'not_run' | 'failed';

export interface NewFormatResult {
  plan?: NfCheck | null;
  skilledNursing?: NfCheck | null;
  skilledNursingStatus?: SkilledNursingStatus;
}

// ---- What the view renders ----
export type CellTone = 'plain' | 'muted' | 'ok' | 'required';

export interface Cell {
  lines: string[];
  tone: CellTone;
  sub?: string;
}

export interface TableRow {
  label: string;
  cells: Cell[];
}

export interface CoverageRow {
  plan: string | null;
  type: string;
  typeSub?: string;
  status: string;
  dates: string;
  datesSub?: string;
}

export interface Contact {
  role: string;
  name: string;
  phone: string;
}

export interface OtherItem {
  label: string;
  value: string | null;
  sub?: string;
}

export interface PayerNote {
  text: string;
  sub: string;
}

export interface AllBenefitsGroup {
  title: string;
  rows: string[][];
}

export interface BenefitsView {
  header: {
    payer: string;
    memberId: string;
    groupNumber: string;
    dob: string;
  };
  coverage: CoverageRow[];
  skilledNursing: { columns: string[]; rows: TableRow[] } | null;
  snfEmpty: string;
  snfHint: string;
  planCosts: { columns: string[]; rows: TableRow[] };
  contacts: Contact[];
  other: OtherItem[];
  notes: PayerNote[];
  footer: string;
  planNumber: string | null;
  allBenefits: AllBenefitsGroup[];
}

// ---- Reading entries ----
interface Located {
  kind: string; // the benefits array the entry came from: statuses, coPayment, …
  entry: NfEntry;
  plan: NfPlan;
  invalid?: boolean;
}

function entriesOf(response: NfResponse | undefined): Located[] {
  const out: Located[] = [];
  for (const plan of response?.plans ?? []) {
    for (const [kind, value] of Object.entries(plan.benefits ?? {})) {
      if (Array.isArray(value)) {
        value.forEach((entry) => out.push({ kind, entry, plan }));
      } else if (kind === 'invalidEntries' && value) {
        for (const [invalidKind, invalid] of Object.entries(value)) {
          (invalid ?? []).forEach((entry) => out.push({ kind: invalidKind, entry, plan, invalid: true }));
        }
      }
    }
  }
  return out;
}

// An entry returned by both checks is shown once (field map: "match on benefit array, service,
// insuranceType, network and plan"). Comparing the whole entry is stricter than that and keeps two
// different amounts for the same service from collapsing into one.
function dedupe(entries: Located[]): Located[] {
  const seen = new Set<string>();
  return entries.filter((located) => {
    const key = `${located.kind}|${located.invalid ? 'invalid' : ''}|${JSON.stringify(located.entry)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const serviceOf = (located: Located) => located.entry.service?.value ?? '';
const isActive = (entry: NfEntry) => Boolean(entry.status?.startsWith('ACTIVE'));
const isInactive = (entry: NfEntry) => Boolean(entry.status?.startsWith('INACTIVE'));

// ---- Formatting ----
const ENUM_WORDS: Record<string, string> = {
  PREFERRED_PROVIDER_ORGANIZATION_PPO: 'Preferred Provider Organization (PPO)',
  HEALTH_MAINTENANCE_ORGANIZATION_HMO: 'Health Maintenance Organization (HMO)',
  HEALTH_MAINTENANCE_ORGANIZATION_HMO_MEDICARE_RISK: 'Health Maintenance Organization (HMO) Medicare Risk',
  POINT_OF_SERVICE_POS: 'Point of Service (POS)',
  EXCLUSIVE_PROVIDER_ORGANIZATION_EPO: 'Exclusive Provider Organization (EPO)',
  INDEMNITY_INSURANCE: 'Indemnity Insurance',
  EMPLOYEE_ONLY: 'Employee only',
  IN_NETWORK: 'In network',
  OUT_OF_NETWORK: 'Out of network',
  IN_AND_OUT_OF_NETWORK: 'All providers',
  U: 'Unknown',
};

// An enum in plain words: a known spelling, else the enum itself with its case softened
// (MEDICARE_PART_A reads "Medicare part a" without this table, so known ones live above).
export function enumWords(value: string | undefined): string {
  if (!value) return '';
  if (ENUM_WORDS[value]) return ENUM_WORDS[value];
  const words = value.toLowerCase().split('_').filter(Boolean).join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const titleWords = (value: string | undefined) => (value ? startCase(value.toLowerCase()) : '');

// Plan types read as names ("Medicare Primary"), unlike the other enums, which read as phrases.
const insuranceTypeWords = (value: string | undefined) => (value && ENUM_WORDS[value]) || titleWords(value);

export function formatDate(value: string | undefined): string | null {
  return value ? formatStediDate(value) : null;
}

export function formatMoney(amount: string | undefined): string | null {
  if (amount === undefined || amount === '') return null;
  const number = Number(amount);
  if (Number.isNaN(number)) return amount;
  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

// Decimal → percent ("0.2" shows 20%).
export function formatPercent(percent: string | undefined): string | null {
  if (percent === undefined || percent === '') return null;
  const number = Number(percent);
  if (Number.isNaN(number)) return percent;
  return `${Math.round(number * 10000) / 100}%`;
}

// The app's phone format, for a US number with or without its leading 1; anything else as returned.
export function formatPhone(phone: string): string {
  const digits = extractDigits(phone).replace(/^1(?=\d{10}$)/, '');
  return digits.length === 10 ? formatPhone10Digits(digits) : phone;
}

const PERIOD_WORDS: Record<string, string> = {
  CALENDAR_YEAR: 'Per calendar year',
  SERVICE_YEAR: 'Per service year',
  CONTRACT: 'Per contract',
  LIFETIME: 'Lifetime',
  VISIT: 'Per visit',
  DAY: 'Per day',
  MONTH: 'Per month',
  YEAR: 'Per year',
  EPISODE: 'Per episode',
  ADMISSION: 'Per admission',
};

function periodWords(timePeriod: string | undefined): string | undefined {
  if (!timePeriod || timePeriod === 'REMAINING') return undefined;
  return PERIOD_WORDS[timePeriod] ?? enumWords(timePeriod);
}

function personOrOrganization(name: NfName | undefined): string {
  if (!name) return '';
  if (name.organization) return name.organization;
  return [name.person?.firstName, name.person?.lastName].filter(Boolean).join(' ');
}

const notReturned = (sub?: string): Cell => ({ lines: [NOT_RETURNED], tone: 'muted', sub });
const plain = (lines: string[], sub?: string): Cell => ({ lines, tone: 'plain', sub });

// ---- Network columns ----
// In network / Out of network when any entry is split by network, else one All providers column.
// An entry that isn't split (IN_AND_OUT_OF_NETWORK, "U", none) applies to both.
function networkColumns(entries: Located[]): { label: string; matches: (l: Located) => boolean }[] {
  const indicator = (l: Located) => l.entry.network?.indicator;
  const split = entries.some((l) => indicator(l) === 'IN_NETWORK' || indicator(l) === 'OUT_OF_NETWORK');
  if (!split) return [{ label: 'All providers', matches: () => true }];
  return [
    { label: 'In network', matches: (l) => indicator(l) !== 'OUT_OF_NETWORK' },
    { label: 'Out of network', matches: (l) => indicator(l) !== 'IN_NETWORK' },
  ];
}

// ---- Cells ----
// Deductible and out-of-pocket max: a remaining amount is paired with its total only within one plan,
// program and coverage level, so two plans' amounts never combine. One group shows bare; several
// (Individual and Family, or two programs) each get their own named line.
function totalAndRemaining(located: Located[]): Cell {
  if (located.length === 0) return notReturned();

  const groups = new Map<string, { name: string; entries: NfEntry[] }>();
  const plans = [...new Set(located.map((l) => l.plan))];
  for (const { entry, plan } of located) {
    const key = `${plans.indexOf(plan)}|${entry.insuranceType ?? ''}|${entry.planCoverageDescription ?? ''}|${entry.coverageLevel ?? ''}`;
    const name = [
      entry.planCoverageDescription || insuranceTypeWords(entry.insuranceType),
      enumWords(entry.coverageLevel),
    ]
      .filter(Boolean)
      .join(', ');
    if (!groups.has(key)) groups.set(key, { name, entries: [] });
    groups.get(key)!.entries.push(entry);
  }
  const lines: string[] = [];
  const subs = new Set<string>();
  for (const { name, entries } of groups.values()) {
    const remaining = entries.find((e) => e.timePeriod === 'REMAINING');
    const totals = entries.filter((e) => e.timePeriod !== 'REMAINING');
    const remainingAmount = formatMoney(remaining?.amount);
    const totalAmount = totals.length === 1 ? formatMoney(totals[0].amount) : null;
    const texts =
      remainingAmount && totalAmount
        ? [`${remainingAmount} of ${totalAmount} remaining`]
        : [
            ...totals.map((e) => formatMoney(e.amount) ?? NOT_RETURNED),
            ...(remainingAmount ? [`${remainingAmount} remaining`] : []),
          ];
    totals.forEach((e) => {
      const period = periodWords(e.timePeriod);
      if (period) subs.add(period);
    });
    const text = texts.join(', ');
    lines.push(groups.size > 1 && name ? `${name}: ${text}` : text);
  }
  return plain(lines, subs.size ? [...subs].join(' · ') : undefined);
}

// Copay: one line per entry with an amount, equal weight. With more than one, each entry's message becomes its tier
// label; a single entry's message goes to Payer Notes instead.
function copay(all: NfEntry[], tierLabels: Set<string>): Cell {
  const entries = all.filter((e) => e.amount !== undefined && e.amount !== '');
  if (entries.length === 0) return notReturned();
  const tiered = entries.length > 1;
  const lines = entries.map((e) => {
    const amount = formatMoney(e.amount) ?? NOT_RETURNED;
    const period = e.timePeriod && e.timePeriod !== 'REMAINING' ? ` ${periodWords(e.timePeriod)?.toLowerCase()}` : '';
    const days = tiered && !e.messages?.length ? tierDays(e) : null;
    const label = tiered && e.messages?.length ? ` · ${e.messages.join(' ')}` : days ? ` · ${days}` : '';
    if (tiered) e.messages?.forEach((m) => tierLabels.add(m));
    return `${amount}${period}${label}`;
  });
  return plain(lines);
}

// Usually a percent, but Medicare sends skilled nursing coinsurance as a daily dollar amount.
function coinsurance(entries: NfEntry[]): Cell {
  const texts = entries
    .map((e) => {
      const percent = formatPercent(e.percent);
      if (percent) return percent;
      const amount = formatMoney(e.amount);
      const period = e.timePeriod && e.timePeriod !== 'REMAINING' ? ` ${periodWords(e.timePeriod)?.toLowerCase()}` : '';
      return amount && `${amount}${period}`;
    })
    .filter((text): text is string => Boolean(text));
  return texts.length ? plain([...new Set(texts)]) : notReturned();
}

// Required wins, then Not required, then any other value the payer sent ("U").
function priorAuth(entries: NfEntry[]): Cell {
  const indicators = entries.map((e) => e.priorAuthIndicator).filter(Boolean);
  if (indicators.includes('REQUIRED')) return { lines: ['Required'], tone: 'required' };
  if (indicators.includes('NOT_REQUIRED')) return plain(['Not required']);
  if (indicators.length > 0) return { lines: ['Not specified by payer'], tone: 'muted' };
  return notReturned();
}

// ---- SNF days ----
interface DayCounts {
  remaining?: string;
  limit?: { value: string; period?: string };
  // A copay tier's days, from its NOT_EXCEEDED / EXCEEDED bounds.
  tier?: { from?: number; to?: number };
}

const COST_SHARE_KINDS = new Set(['coPayment', 'coInsurance']);
// Payer text that can speak to SNF days. Disclaimers and amounts mention days for other reasons
// ("claims must be filed within 90 days").
const DAY_TEXT_KINDS = new Set(['statuses', 'limitations', 'exclusions', 'nonCovered']);
const MENTIONS_DAYS = /\bdays?\b/i;

// Day counts in either place a payer sends them: the entry's own `quantity` (with REMAINING or a period
// as its time period), or its `serviceLimits`.
function dayCounts(entry: NfEntry): DayCounts {
  const counts: DayCounts = {};
  if (entry.quantity?.qualifier === 'DAYS' && entry.quantity.value) {
    if (entry.timePeriod === 'REMAINING') counts.remaining = entry.quantity.value;
    else counts.limit = { value: entry.quantity.value, period: entry.timePeriod };
  }
  for (const limit of entry.serviceLimits ?? []) {
    // A limit counted in something other than days (visits) isn't a day count.
    const quantity = limit.quantity ?? limit.delivery?.quantity;
    if (quantity?.qualifier && quantity.qualifier !== 'DAYS') continue;
    const period = limit.delivery?.period;
    const days = quantity?.value || undefined;
    const value = period?.value !== undefined && period.value !== '' ? String(period.value) : undefined;
    if (period?.qualifier === 'REMAINING') {
      if (days ?? value) counts.remaining = days ?? value;
    } else if (period?.qualifier === 'NOT_EXCEEDED' && (value ?? days)) {
      counts.tier = { ...counts.tier, to: Number(value ?? days) };
    } else if (period?.qualifier === 'EXCEEDED' && (value ?? days)) {
      counts.tier = { ...counts.tier, from: Number(value ?? days) + 1 };
    } else if (days) {
      counts.limit = { value: days, period: period?.qualifier };
    }
  }
  return counts;
}

interface Counted extends Located {
  counts: DayCounts;
}

const isCostShareTier = (c: Counted) => COST_SHARE_KINDS.has(c.kind) && Boolean(c.counts.tier);
const tierKey = (c: Counted) => `${c.counts.tier!.from ?? 1}-${c.counts.tier!.to ?? ''}`;

// "80 days at $217/day": the tier's share with its own period, as the Copay row words it.
function tierText({ entry, counts }: Counted): string {
  const amount = formatMoney(entry.amount);
  const share = amount ?? formatPercent(entry.percent);
  if (share === '$0' || share === '0%') return `${counts.remaining} full-coverage days`;
  if (!share) return `${counts.remaining} days`;
  if (!amount) return `${counts.remaining} days at ${share}`;
  const words = periodWords(entry.timePeriod)?.toLowerCase();
  const period = entry.timePeriod === 'DAY' ? '/day' : words ? ` ${words}` : '';
  return `${counts.remaining} days at ${share}${period}`;
}

// The first of these the payer returns, the same way for every payer (field map, "SNF days remaining
// (any payer)"): remaining days; remaining days per copay tier, which is how traditional Medicare
// answers; a day limit alone; days mentioned only in text. Nothing is subtracted or read out of text.
// Text it shows goes into `dayTexts`, so Payer Notes doesn't repeat it.
function daysRemaining(entries: Located[], tierLabels: Set<string>, dayTexts: Set<string>): Cell {
  const counted: Counted[] = entries.map((located) => ({ ...located, counts: dayCounts(located.entry) }));
  const outsideTiers = counted.filter((c) => !isCostShareTier(c));

  const remainingEntry = outsideTiers.find((c) => c.counts.remaining);
  const total = outsideTiers.find((c) => c.counts.limit)?.counts.limit;
  if (remainingEntry) {
    const remaining = remainingEntry.counts.remaining;
    // Only a total from the same plan, so two plans' counts never pair up.
    const ownTotal = outsideTiers.find((c) => c.counts.limit && c.plan === remainingEntry.plan)?.counts.limit;
    if (!ownTotal) return plain([`${remaining} days remaining`]);
    return plain([`${remaining} of ${ownTotal.value} days remaining`], periodWords(ownTotal.period));
  }

  // Per tier only when every tier says how many days it has left; otherwise the limit below. Where a
  // tier comes as both a copay and a coinsurance, the one with an amount wins, then the copay. Tiers
  // come from one plan only, like the remaining days above.
  const ranked = counted
    .filter(isCostShareTier)
    .sort(
      (a, b) =>
        Number(Boolean(b.counts.remaining)) - Number(Boolean(a.counts.remaining)) ||
        Number(Boolean(b.entry.amount || b.entry.percent)) - Number(Boolean(a.entry.amount || a.entry.percent)) ||
        Number(b.kind === 'coPayment') - Number(a.kind === 'coPayment')
    );
  const tiers = ranked.filter((c) => c.plan === ranked[0]?.plan);
  const byTier = new Map<string, Counted>();
  tiers.forEach((c) => byTier.has(tierKey(c)) || byTier.set(tierKey(c), c));
  const distinct = [...byTier.values()].sort((a, b) => (a.counts.tier!.from ?? 1) - (b.counts.tier!.from ?? 1));
  if (distinct.length && distinct.every((c) => c.counts.remaining)) return plain([distinct.map(tierText).join(' · ')]);

  if (total)
    return plain(
      [`Up to ${total.value} days ${periodWords(total.period)?.toLowerCase() ?? ''}`.trim()],
      'Remaining not returned'
    );
  // A last tier with no end ("Days 21 and up") means the tiers don't say how many days there are.
  const openEnded = distinct.some((c) => !c.counts.tier?.to);
  const tierEnd = Math.max(0, ...distinct.map((c) => c.counts.tier?.to ?? 0));
  if (tierEnd > 0 && !openEnded) return plain([`Up to ${tierEnd} days`], 'Remaining not returned');

  const text = [
    ...new Set(
      entries
        .filter((l) => DAY_TEXT_KINDS.has(l.kind))
        .flatMap((l) => l.entry.messages ?? [])
        .filter((m) => MENTIONS_DAYS.test(m) && !tierLabels.has(m))
    ),
  ];
  text.forEach((m) => dayTexts.add(m));
  return notReturned(text.length ? text.join(' ') : undefined);
}

// "Days 1–20" for a copay tier the payer sent without a message to label it.
function tierDays(entry: NfEntry): string | null {
  const tier = dayCounts(entry).tier;
  if (!tier) return null;
  const from = tier.from ?? 1;
  return tier.to ? `Days ${from}–${tier.to}` : `Days ${from} and up`;
}

function snfCoverage(entries: Located[]): Cell {
  const statuses = entries.filter((l) => l.kind === 'statuses');
  const nonCovered = entries.filter((l) => l.kind === 'nonCovered');
  if (statuses.length === 0 && nonCovered.length === 0) return notReturned();

  const active = statuses.filter((l) => isActive(l.entry));
  const under = [...new Set(active.map((l) => l.entry.planCoverageDescription).filter(Boolean))] as string[];
  const notUnder = [...new Set(nonCovered.map((l) => l.entry.planCoverageDescription).filter(Boolean))] as string[];
  // Which plans cover it goes in the grey line once there's more than one plan to tell apart.
  const parts: string[] = [];
  if (under.length && (under.length > 1 || notUnder.length)) parts.push(`Under ${joinWords(under)}.`);
  if (notUnder.length) parts.push(`Not covered under ${joinWords(notUnder)}.`);
  const sub = parts.join(' ') || undefined;

  if (active.length > 0) return { lines: ['Covered'], tone: 'ok', sub };
  return plain(['Not covered'], sub);
}

function joinWords(words: string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

// ---- Sections ----
function header(plan: NfResponse): BenefitsView['header'] {
  const person = plan.dependent ?? plan.subscriber;
  const info = plan.subscriber?.additionalInformation;
  return {
    payer: personOrOrganization(plan.payer?.name) || NOT_RETURNED,
    memberId: person?.memberId || plan.subscriber?.memberId || NOT_RETURNED,
    groupNumber: info?.group?.number || info?.groupNumber?.number || NOT_RETURNED,
    dob: formatDate(person?.dateOfBirth) ?? NOT_RETURNED,
  };
}

const DATE_KEYS = ['benefit', 'plan', 'eligibility', 'period'];

function datesFor(entry: NfEntry, plan: NfResponse): NfDates | undefined {
  for (const key of DATE_KEYS) if (entry.dates?.[key]?.start) return entry.dates[key];
  const subscriberDates = (plan.dependent ?? plan.subscriber)?.dates;
  return subscriberDates?.plan ?? subscriberDates?.eligibility;
}

function coverage(plan: NfResponse, planEntries: Located[]): CoverageRow[] {
  const seen = new Set<string>();
  return planEntries
    .filter((l) => l.kind === 'statuses' && serviceOf(l) === PLAN_LEVEL && !l.invalid)
    .filter((l) => {
      const key = `${l.entry.planCoverageDescription}|${l.entry.status}|${l.entry.insuranceType}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(({ entry, plan: benefitPlan }) => {
      const dates = datesFor(entry, plan);
      const start = formatDate(dates?.start);
      const end = formatDate(dates?.end);
      const related = [...(benefitPlan.relatedEntities ?? []), ...(entry.relatedEntities ?? [])]
        .filter((r) => !r.type || !NOT_A_CONTACT.has(r.type))
        .map((r) => personOrOrganization(r.name))
        .filter(Boolean);
      const typeSub = [entry.coverageLevel ? enumWords(entry.coverageLevel) : '', ...related]
        .filter(Boolean)
        .join(' · ');
      const status = isInactive(entry)
        ? 'Inactive'
        : isActive(entry)
          ? 'Active'
          : enumWords(entry.status) || NOT_RETURNED;
      return {
        plan: entry.planCoverageDescription || benefitPlan.name || null,
        type: insuranceTypeWords(entry.insuranceType) || NOT_RETURNED,
        typeSub: typeSub || undefined,
        status: entry.status?.includes('PARTIAL') ? `${status} (partial)` : status,
        dates: start ? `${start} – ${end ?? 'no end date returned'}` : NOT_RETURNED,
        datesSub: start && start === end ? 'Eligibility returned for the date checked only' : undefined,
      };
    });
}

function snfSection(entries: Located[], tierLabels: Set<string>): BenefitsView['skilledNursing'] {
  if (entries.length === 0) return null;
  const columns = networkColumns(entries);
  const located = (kind: string, column: (typeof columns)[number]) =>
    entries.filter((l) => l.kind === kind && column.matches(l));
  const per = (kind: string, column: (typeof columns)[number]) => located(kind, column).map((l) => l.entry);
  const dayTexts = new Set<string>();

  const rows: TableRow[] = [
    { label: 'Coverage', cells: columns.map((c) => snfCoverage(entries.filter(c.matches))) },
    { label: 'Copay', cells: columns.map((c) => copay(per('coPayment', c), tierLabels)) },
    { label: 'Coinsurance', cells: columns.map((c) => coinsurance(per('coInsurance', c))) },
    { label: 'Deductible', cells: columns.map((c) => totalAndRemaining(located('deductible', c))) },
    { label: 'Out-of-pocket max', cells: columns.map((c) => totalAndRemaining(located('outOfPocket', c))) },
    { label: 'Prior auth', cells: columns.map((c) => priorAuth(entries.filter(c.matches).map((l) => l.entry))) },
    {
      label: 'SNF days remaining',
      cells: columns.map((c) => daysRemaining(entries.filter(c.matches), tierLabels, dayTexts)),
    },
  ];
  dayTexts.forEach((text) => tierLabels.add(text));
  return { columns: columns.map((c) => c.label), rows };
}

// The codes the payer actually returned, and a note when nothing was split by network.
function snfHint(entries: Located[], section: BenefitsView['skilledNursing']): string {
  const codes = [...SKILLED_NURSING].filter((code) => entries.some((l) => serviceOf(l) === code));
  const hint = `STC ${codes.length ? codes.join(' / ') : 'AG / AH'}`;
  return section?.columns.length === 1 ? `${hint} · network not applicable for this plan` : hint;
}

function planCostsSection(entries: Located[]): BenefitsView['planCosts'] {
  const columns = networkColumns(entries.filter((l) => l.kind === 'deductible' || l.kind === 'outOfPocket'));
  const located = (kind: string, column: (typeof columns)[number]) =>
    entries.filter((l) => l.kind === kind && column.matches(l));
  return {
    columns: columns.map((c) => c.label),
    rows: [
      { label: 'Deductible', cells: columns.map((c) => totalAndRemaining(located('deductible', c))) },
      { label: 'Out-of-pocket max', cells: columns.map((c) => totalAndRemaining(located('outOfPocket', c))) },
    ],
  };
}

// Anyone with a phone number, from either check. The payer itself, whoever ran the check, the
// provider asking and the patient are left off: they're not someone to call about this patient.
const NOT_A_CONTACT = new Set([
  'PAYER',
  'PARTY_PERFORMING_VERIFICATION',
  'PROVIDER',
  'INFORMATION_RECEIVER',
  'INSURED_OR_SUBSCRIBER',
]);

// The arrays the summary reads. A contact found on any other array (managedCareCoordinator, …) says
// which in its role, since that's what the payer named them for.
const SUMMARY_KINDS = new Set([
  'statuses',
  'coPayment',
  'coInsurance',
  'deductible',
  'outOfPocket',
  'limitations',
  'nonCovered',
  // "Contact the following entity for information" says nothing the role doesn't.
  'contactFollowingEntityForInformation',
]);

function contacts(entries: Located[]): Contact[] {
  const out: Contact[] = [];
  const seen = new Set<string>();
  const entities: { entity: NfRelatedEntity; kind?: string }[] = [
    ...entries.flatMap((l) => (l.entry.relatedEntities ?? []).map((entity) => ({ entity, kind: l.kind }))),
    ...[...new Set(entries.flatMap((l) => l.plan.relatedEntities ?? []))].map((entity) => ({ entity })),
  ];
  for (const { entity, kind } of entities) {
    if (entity.type && NOT_A_CONTACT.has(entity.type)) continue;
    const name = personOrOrganization(entity.name);
    for (const phone of (entity.contacts ?? []).flatMap((c) => c.phoneNumbers ?? [])) {
      const formatted = formatPhone(phone);
      const key = `${name}|${formatted}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const role = enumWords(entity.type) || 'Contact';
      const kindWords = kind ? startCase(kind).toLowerCase() : '';
      const from = kindWords && !SUMMARY_KINDS.has(kind!) && kindWords !== role.toLowerCase() ? ` (${kindWords})` : '';
      out.push({ role: `${role}${from}`, name: name || NOT_RETURNED, phone: formatted });
    }
  }
  return out;
}

// Every path in this section is "to confirm" in the field map. Read where a payer's own label says
// what it is, never inferred.
const OTHER_PAYER_TYPES = new Set(['PRIMARY_PAYER', 'SECONDARY_PAYER', 'TERTIARY_PAYER', 'OTHER_PAYER']);
const PATIENT_LIABILITY_KINDS = new Set(['spendDown', 'patientLiability']);

const OTHER_PAYER_KINDS = new Set(['otherOrAdditionalPayer']);

// A Medicare Beneficiary Identifier reads in its printed 4-3-4 groups.
function formatMbi(id: string): string {
  const compact = id.replace(/[\s-]/g, '');
  return compact.length === 11 ? `${compact.slice(0, 4)}-${compact.slice(4, 7)}-${compact.slice(7)}` : id;
}

function other(entries: Located[], shown: Set<string>): OtherItem[] {
  const labeled = entries.flatMap((l) =>
    (l.entry.relatedEntities ?? []).filter((r) => r.type && OTHER_PAYER_TYPES.has(r.type))
  );
  // An other-payer entry names the payer on a payer entity, and the patient's ID with that payer on
  // the insured entity beside it, whose name (the patient's) is never shown.
  const otherEntries = entries.filter((l) => OTHER_PAYER_KINDS.has(l.kind));
  const listed = otherEntries.flatMap((l) => {
    const payerEntities = (l.entry.relatedEntities ?? []).filter(
      (r) => r.type === 'PAYER' || OTHER_PAYER_TYPES.has(r.type ?? '')
    );
    return payerEntities.length ? payerEntities : [{ name: { organization: l.entry.planCoverageDescription } }];
  });
  const payerEntities = [...labeled, ...listed];
  const payers = [...new Set(payerEntities.map((r) => personOrOrganization(r.name)).filter(Boolean))];
  const ids = [
    ...new Set(
      [...labeled, ...otherEntries.flatMap((l) => l.entry.relatedEntities ?? [])].map((r) => r.memberId).filter(Boolean)
    ),
  ] as string[];
  const medicare = payers.length > 0 && payers.every((p) => p.startsWith('Medicare'));
  const idLine = ids.length
    ? `${medicare ? 'Medicare ID (MBI)' : 'Member ID'} ${ids.map((id) => (medicare ? formatMbi(id) : id)).join(', ')}`
    : undefined;

  const liability = entries.find((l) => PATIENT_LIABILITY_KINDS.has(l.kind) && l.entry.amount);
  liability?.entry.messages?.forEach((m) => shown.add(m));
  const message = liability?.entry.messages?.join(' ');
  const liabilityLabel = enumWords(liability?.entry.insuranceType);
  return [
    { label: 'Other payers on file', value: payers.length ? joinWords(payers) : null, sub: idLine },
    {
      label: 'Patient liability',
      value: liability ? formatMoney(liability.entry.amount) : null,
      sub: message && liabilityLabel ? `${liabilityLabel} · \u201c${message}\u201d` : message || undefined,
    },
  ];
}

// The programs active for an entry's service in its own plan, for a message that names none itself.
function activePlansFor(entries: Located[], plan: NfPlan, service: string | undefined): string[] {
  const names = entries
    .filter((l) => l.plan === plan && l.kind === 'statuses' && serviceOf(l) === service)
    .filter((l) => isActive(l.entry))
    .map((l) => l.entry.planCoverageDescription)
    .filter(Boolean) as string[];
  return [...new Set(names)];
}

// Messages on skilled nursing and plan-level entries that no other section already shows.
function notes(snfEntries: Located[], planLevel: Located[], shown: Set<string>, all: Located[]): PayerNote[] {
  const out: PayerNote[] = [];
  const seen = new Set<string>(shown);
  const add = (located: Located[], source: string) => {
    for (const { entry, plan } of located) {
      for (const message of entry.messages ?? []) {
        if (seen.has(message)) continue;
        seen.add(message);
        const names = entry.planCoverageDescription
          ? [entry.planCoverageDescription]
          : activePlansFor(all, plan, entry.service?.value);
        const plans = names.length ? ` · ${joinWords(names)}` : '';
        out.push({ text: message, sub: `${source}${plans}` });
      }
    }
  };
  add(
    snfEntries.filter((l) => l.kind !== 'benefitDisclaimer'),
    'Skilled nursing'
  );
  add(
    planLevel.filter((l) => l.kind !== 'benefitDisclaimer'),
    'Plan level'
  );
  return out;
}

function footer(entries: Located[], snfCodes: string[] | null): string {
  const checks = snfCodes
    ? `Combines 2 checks: skilled nursing (${snfCodes.join(', ')}) and plan coverage (30).`
    : 'Plan coverage check (30).';
  const disclaimers = [
    ...new Set(entries.filter((l) => l.kind === 'benefitDisclaimer').flatMap((l) => l.entry.messages ?? [])),
  ];
  return [checks, ...disclaimers, 'Not a guarantee of payment.'].join(' ');
}

// ---- Show all benefits ----
// Worded the way today's view words a benefit line (field map: "Unchanged from today's staging view"):
// the X12 benefit names, Yes / No for network, and amounts as the payer sent them.
const BENEFIT_NAMES: Record<string, string> = {
  coPayment: 'Co-Payment',
  coInsurance: 'Co-Insurance',
  outOfPocket: 'Out of Pocket (Stop Loss)',
  nonCovered: 'Non-Covered',
  otherOrAdditionalPayer: 'Other or Additional Payor',
};

const NETWORK_WORDS: Record<string, string> = {
  IN_NETWORK: 'Yes',
  OUT_OF_NETWORK: 'No',
  IN_AND_OUT_OF_NETWORK: 'Not Applicable',
  NOT_APPLICABLE: 'Not Applicable',
  U: 'Unknown',
};

function coverageText(located: Located, statusCount: number): string {
  const { entry, kind } = located;
  if (kind === 'statuses') {
    const verdict = isInactive(entry) ? 'Not covered' : 'Covered';
    return statusCount > 1 && entry.planCoverageDescription ? `${verdict} · ${entry.planCoverageDescription}` : verdict;
  }
  const parts: string[] = [];
  if (entry.percent || entry.amount) {
    parts.push(
      benefitCoverageText({
        benefitPercent: entry.percent,
        benefitAmount: entry.amount,
        timeQualifier: titleWords(entry.timePeriod),
      })
    );
  }
  if (entry.quantity?.value) parts.push(`${entry.quantity.value} ${enumWords(entry.quantity.qualifier).toLowerCase()}`);
  if (entry.planCoverageDescription) parts.push(entry.planCoverageDescription);
  if (parts.length === 0 && kind === 'nonCovered') return 'Not covered';
  return parts.join(' · ') || '—';
}

function allBenefits(entries: Located[]): AllBenefitsGroup[] {
  const groups = new Map<string, { code: string; title: string; located: Located[] }>();
  for (const located of entries) {
    const code = serviceOf(located);
    if (!groups.has(code))
      groups.set(code, { code, title: code ? `STC ${code}` : 'Other benefit information', located: [] });
    const group = groups.get(code)!;
    const definition = located.entry.service?.definition;
    if (code && definition && group.title === `STC ${code}`) group.title = `STC ${code} ${definition}`;
    group.located.push(located);
  }
  // Plan level first, then skilled nursing, then the rest as returned, with entries that name no
  // service type last.
  const rank = (code: string) => (code === PLAN_LEVEL ? 0 : SKILLED_NURSING.has(code) ? 1 : code ? 2 : 3);
  const ordered = [...groups.values()].sort((a, b) => rank(a.code) - rank(b.code));
  return ordered.map(({ title, located }) => {
    const statusCount = located.filter((l) => l.kind === 'statuses').length;
    return {
      title,
      rows: located.map((l) => {
        const { entry } = l;
        const type =
          l.kind === 'statuses'
            ? titleWords(entry.status?.replace(/_PARTIAL$/, ''))
            : (BENEFIT_NAMES[l.kind] ?? startCase(l.kind));
        const detail = [
          ...(entry.status?.includes('PARTIAL') ? ['Partial'] : []),
          ...(entry.messages ?? []),
          ...(l.invalid ? (entry.invalidReasons ?? []).map((r) => r.description ?? '') : []),
        ].filter(Boolean);
        const indicator = entry.network?.indicator;
        return [
          l.invalid ? `${type} (invalid)` : type,
          titleWords(entry.coverageLevel) || '—',
          (indicator && (NETWORK_WORDS[indicator] ?? titleWords(indicator))) || '—',
          coverageText(l, statusCount),
          detail.join(' ') || '—',
        ];
      }),
    };
  });
}

// ---- The view ----
export function buildBenefitsView(newFormat: NewFormatResult): BenefitsView | null {
  const plan = newFormat.plan?.stediResponse;
  if (!hasPlans(plan)) return null;
  const snfResponse = hasPlans(newFormat.skilledNursing?.stediResponse)
    ? newFormat.skilledNursing?.stediResponse
    : undefined;

  const planEntries = entriesOf(plan);
  const all = dedupe([...planEntries, ...entriesOf(snfResponse)]);
  const valid = all.filter((l) => !l.invalid);
  const snfEntries = valid.filter((l) => SKILLED_NURSING.has(serviceOf(l)));
  // Payers repeat the plan-level entries in the skilled nursing response, and a copy that differs in
  // any field would survive the dedupe and break the total-and-remaining pairing. The plan-level
  // check is the one that answers for 30.
  const planLevel = planEntries.filter((l) => !l.invalid && serviceOf(l) === PLAN_LEVEL);
  const status = newFormat.skilledNursingStatus;
  // The current endpoint's skilled nursing check ran even when the new one didn't answer.
  const snfRan = Boolean(snfResponse) || status === 'not_run' || status === 'failed';
  const snfCodes = snfRan ? (newFormat.skilledNursing?.serviceTypeCodes ?? ['AG']) : null;

  // Messages a section already shows (copay tier labels, the patient liability message), so Payer
  // Notes doesn't repeat them. Filled by the sections built before it.
  const shown = new Set<string>();
  const skilledNursing = snfSection(snfEntries, shown);
  const otherCoverage = other(valid, shown);

  return {
    header: header(plan),
    coverage: coverage(plan, planEntries),
    skilledNursing,
    snfEmpty: !snfResponse && (status === 'not_run' || status === 'failed') ? SNF_NOT_CHECKED : SNF_NOT_REPORTED,
    snfHint: snfHint(snfEntries, skilledNursing),
    planCosts: planCostsSection(planLevel),
    contacts: contacts(valid),
    other: otherCoverage,
    notes: notes(snfEntries, planLevel, shown, valid),
    footer: footer(valid, snfCodes),
    planNumber: plan.subscriber?.additionalInformation?.plan?.number ?? null,
    allBenefits: allBenefits(all),
  };
}

// The new view shows only for a check with a new-format plan-level response; anything run before the
// new-format setting was on keeps today's layout.
// A payer reject comes back with no plans, so it doesn't count as a response.
const hasPlans = (response: NfResponse | undefined): response is NfResponse => Boolean(response?.plans?.length);

export function parseNewFormat(value: unknown): NewFormatResult | null {
  if (typeof value !== 'object' || value === null) return null;
  const newFormat = (value as { newFormat?: NewFormatResult }).newFormat;
  return newFormat && hasPlans(newFormat.plan?.stediResponse) ? newFormat : null;
}
